import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  FieldErrors,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister
} from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import Input from '@components/atoms/ui-kit/input';
import Button from '@components/atoms/ui-kit/button';
import { WizardFormData, WizardSubOrg } from '../create-customer-wizard-types';

interface SubOrgField {
  id: string;
}

interface Props {
  register: UseFormRegister<WizardFormData>;
  errors: FieldErrors<WizardFormData>;
  subOrgFields: SubOrgField[];
  appendSubOrg: UseFieldArrayAppend<WizardFormData, 'sub_organizations'>;
  removeSubOrg: UseFieldArrayRemove;
}

export function CustomerWizardStep4({
  register,
  errors,
  subOrgFields,
  appendSubOrg,
  removeSubOrg
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const emptySubOrg: WizardSubOrg = { name: '', email: '', users: [] };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4
            className="text-sm font-medium"
            style={{ color: getColour('normalText') }}
          >
            {t('sub-organizations')}
          </h4>
          <p className="text-xs" style={{ color: getColour('inactiveText') }}>
            {t('sub-orgs-step-description')}
          </p>
        </div>
        <Button
          icon={<Plus className="h-4 w-4" />}
          label={t('add-sub-organization')}
          size="small"
          variant="smoke"
          onClick={() => appendSubOrg(emptySubOrg)}
        />
      </div>

      {subOrgFields.length === 0 ? (
        <p
          className="text-sm p-4 rounded-2xl text-center"
          style={{
            backgroundColor: getColour('thirdLayer'),
            color: getColour('inactiveText')
          }}
        >
          {t('no-sub-organizations-added')}
        </p>
      ) : (
        <div className="space-y-4 max-h-80 overflow-y-auto">
          {subOrgFields.map((field, index) => (
            <div
              key={field.id}
              className="p-4 rounded-2xl border"
              style={{
                borderColor: getColour('hairline'),
                backgroundColor: getColour('appSoft')
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <span
                  className="text-sm font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('sub-organization')} {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeSubOrg(index)}
                  className="p-1 rounded-2xl hover:bg-red-100"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label={t('organization-name')}
                  {...register(`sub_organizations.${index}.name`)}
                  error={errors.sub_organizations?.[index]?.name?.message}
                  required
                />
                <Input
                  label={t('email')}
                  type="email"
                  {...register(`sub_organizations.${index}.email`)}
                  error={errors.sub_organizations?.[index]?.email?.message}
                  required
                />
              </div>

              <p
                className="text-xs mt-3"
                style={{ color: getColour('inactiveText') }}
              >
                {t('sub-org-inherits-settings')}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
